import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { exportToExcel } from "@/utils/excelExportOptions";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { get } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { BilletsYardStatus } from "../models/BilletsYardStatus";
import { getBilletsYardStatus } from "../repositories/BilletsYardStatusRepository";
import { BilletsYardStatusTable } from "./components/BilletsYardStatusTable";

interface BilletsYardStatusPageProps {
  status?: BilletsYardStatus[];
  isLoading?: boolean;
  error?: string;
}

export const BilletsYardStatusPage = ({
  status: propStatus,
  isLoading: propIsLoading,
  error: propError,
}: BilletsYardStatusPageProps = {}) => {
  const [status, setStatus] = useState<BilletsYardStatus[]>(propStatus || []);
  const [isLoadingGrid, setIsLoadingGrid] = useState(propIsLoading || false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(propError || "");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const getDataFromAPI = useCallback(async () => {
    if (!propStatus) {
      setIsLoadingGrid(true);

      const resp = await getBilletsYardStatus();
      if (resp.ok) {
        setStatus(get(resp, "data.tables[0].rows", []));
      } else {
        setErrorMessage(resp.message);
      }
      setIsLoadingGrid(false);
    }
  }, [propStatus]);

  const handleExportToExcel = useCallback(() => {
    if (!gridApi) {
      setNotificationMessage("Please wait until the table is loaded");
      return;
    }

    setIsExporting(true);
    try {
      exportToExcel({
        gridApi: gridApi,
        fileName: "billets_yard_status",
        sheetName: "Billets Yard Status",
        startDate: new Date(),
        endDate: new Date(),
        additionalAppendContent: [
          {
            label: "Total Records:",
            value: (status?.length || 0).toString(),
          },
        ],
      });
      setNotificationMessage("Export to Excel completed successfully");
    } catch (error) {
      setErrorMessage("Failed to export to Excel");
    } finally {
      setIsExporting(false);
    }
  }, [gridApi, status]);

  useEffect(() => {
    getDataFromAPI();
  }, [getDataFromAPI]);

  return (
    <>
      <HelmetDexteel title={`Billets Yard Status`} />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Billets Yard Status
          </Typography>
        </Grid>
        <Grid item xs={12} container justifyContent="flex-end">
          <ExcelButton
            handleExportToExcel={handleExportToExcel}
            disabled={!gridApi || isLoadingGrid || isExporting}
            label={isExporting ? "EXPORTING..." : "EXPORT TO EXCEL"}
          />
        </Grid>
        <Grid item xs={12}>
          <BilletsYardStatusTable
            loadingGrid={isLoadingGrid}
            rows={status}
            onGridReady={(params) => setGridApi(params.api)}
          />
        </Grid>
      </Grid>
      <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
